import React, { useState, useRef } from 'react';
import { Upload, AlertCircle, CheckCircle, FileDown, X } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';
import * as XLSX from 'xlsx';
import './ImportModal.css';

const ImportModal = ({ isOpen, onClose, onImport, title, templateHeaders, sampleRow }) => {
    const [file, setFile] = useState(null);
    const [status, setStatus] = useState('idle'); // idle, processing, complete, error
    const [progress, setProgress] = useState(0);
    const [summary, setSummary] = useState({ total: 0, imported: 0, duplicates: 0, errors: 0 });
    const fileInputRef = useRef(null);

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setFile(e.target.files[0]);
            setStatus('idle');
            setSummary({ total: 0, imported: 0, duplicates: 0, errors: 0 });
        }
    };

    const handleDownloadTemplate = () => {
        const ws = XLSX.utils.json_to_sheet([sampleRow], { header: templateHeaders });
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, title.replace('Import ', '') || "Sheet1");
        XLSX.writeFile(wb, `${title.replace(/\s+/g, '_')}_Template.csv`);
    };

    const processFile = async () => {
        if (!file) return;

        setStatus('processing');
        setProgress(0);

        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });

                let combinedData = {};
                let totalRows = 0;

                workbook.SheetNames.forEach(sheetName => {
                    const worksheet = workbook.Sheets[sheetName];
                    const jsonData = XLSX.utils.sheet_to_json(worksheet);
                    if (jsonData.length > 0) {
                        combinedData[sheetName] = jsonData;
                        totalRows += jsonData.length;
                    }
                });

                if (totalRows === 0) {
                    setStatus('error');
                    return;
                }

                setSummary(prev => ({ ...prev, total: totalRows }));

                // Simulate processing with progress (or actally pass progress callback)
                const result = await onImport(combinedData, (percent) => setProgress(percent));

                setSummary(prev => ({
                    ...prev,
                    imported: result.imported,
                    duplicates: result.duplicates,
                    errors: result.errors
                }));
                setStatus('complete');
                setProgress(100);

            } catch (error) {
                console.error("Import Parsing Error", error);
                setStatus('error');
            }
        };
        reader.readAsArrayBuffer(file);
    };

    if (!isOpen) return null;

    return (
        <div className="import-modal-overlay">
            <div className="import-modal-content">
                <div className="import-header">
                    <div className="import-header-left">
                        <div className="import-icon-wrapper">
                            <Upload size={20} strokeWidth={2.5} />
                        </div>
                        <div className="import-title">
                            <h3>Import Data</h3>
                            <p className="import-subtitle">{title}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="close-btn"><X size={20} /></button>
                </div>

                <div className="import-body">
                    {status === 'complete' ? (
                        <div className="success-state">
                            <div className="success-icon">
                                <CheckCircle size={40} strokeWidth={2.5} />
                            </div>
                            <h4 className="success-title">Import Successful</h4>
                            <div className="stats-grid">
                                <div className="stat-card added">
                                    <div className="stat-value">{summary.imported}</div>
                                    <div className="stat-label">Added</div>
                                </div>
                                <div className="stat-card skipped">
                                    <div className="stat-value">{summary.duplicates}</div>
                                    <div className="stat-label">Skipped</div>
                                </div>
                                <div className="stat-card failed">
                                    <div className="stat-value">{summary.errors}</div>
                                    <div className="stat-label">Failed</div>
                                </div>
                            </div>
                            <button onClick={onClose} className="done-btn">
                                Done
                            </button>
                        </div>
                    ) : (
                        <>
                            {/* File Upload Area */}
                            <div
                                className={`dropzone ${file ? 'active' : ''}`}
                                onDragOver={e => e.preventDefault()}
                                onDrop={e => {
                                    e.preventDefault();
                                    if (e.dataTransfer.files[0]) setFile(e.dataTransfer.files[0]);
                                }}
                                onClick={() => !file && fileInputRef.current?.click()}
                            >
                                <input
                                    type="file"
                                    accept=".csv, .xlsx"
                                    onChange={handleFileChange}
                                    style={{ display: 'none' }}
                                    ref={fileInputRef}
                                />

                                {status === 'processing' ? (
                                    <div className="processing-state">
                                        <LoadingSpinner size={48} color="indigo-600" />
                                        <div className="processing-text">Processing Data...</div>
                                        <div className="progress-bar-bg">
                                            <div className="progress-bar-fill" style={{ width: `${progress}%` }}></div>
                                        </div>
                                        <div className="progress-percent">{progress}% Complete</div>
                                    </div>
                                ) : file ? (
                                    <div className="file-selected">
                                        <div className="file-icon-wrapper">
                                            <FileDown size={32} />
                                        </div>
                                        <div className="file-info">
                                            <div className="file-name">{file.name}</div>
                                            <div className="file-size">{(file.size / 1024).toFixed(1)} KB</div>
                                        </div>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); setFile(null); }}
                                            className="remove-btn"
                                        >
                                            Remove File
                                        </button>
                                    </div>
                                ) : (
                                    <div className="empty-state">
                                        <div className="upload-icon-circle">
                                            <Upload size={36} strokeWidth={1.5} />
                                        </div>
                                        <div>
                                            <div className="empty-text-main">Click to Upload</div>
                                            <div className="empty-text-sub">or drag and drop Excel/CSV files here</div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Template Download */}
                            <div className="template-section">
                                <div className="template-info">
                                    <div className="info-icon">
                                        <AlertCircle size={18} />
                                    </div>
                                    <div className="template-text">Unsure about the format?</div>
                                </div>
                                <button
                                    onClick={handleDownloadTemplate}
                                    className="download-btn"
                                >
                                    <FileDown size={16} /> Download Template
                                </button>
                            </div>

                            <button
                                onClick={processFile}
                                disabled={!file || status === 'processing'}
                                className="run-import-btn"
                            >
                                {status === 'processing' ? 'Processing...' : 'Run Import'}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ImportModal;
